// WishArchiveFilter — desktop filter rail for /wishes/archive (per
// `docs/spec/wishlist/DESIGN.md §5.8`).
//
// 4 filter dimensions:
//   - Year (calendar year of completed_at, multi-select via chip group)
//   - Category (multi-select chip group)
//   - Priority (multi-select chip group)
//   - Completion date range (date pair; min/max percent reserved)
//
// All values flow into a single `value` prop that the route binds to the
// query string via React Router. The rail stays presentational.

import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { WishCategory, WishPriority } from "@/lib/api/wishlist";

export interface WishArchiveFilterValue {
  years: number[];
  categories: WishCategory[];
  priorities: WishPriority[];
  completedFrom: string;
  completedTo: string;
}

export interface WishArchiveFilterProps {
  value: WishArchiveFilterValue;
  onChange: (next: WishArchiveFilterValue) => void;
  /** Available years (auto-derived from data; empty = show last 5 years). */
  availableYears: number[];
  className?: string;
}

const CATEGORY_OPTIONS: { value: WishCategory; label: string }[] = [
  { value: "general", label: "一般" },
  { value: "savings", label: "儲蓄" },
  { value: "travel", label: "旅行" },
  { value: "event", label: "事件" },
  { value: "purchase", label: "購物" },
  { value: "bucket_list", label: "願望清單" },
  { value: "custom", label: "自訂" },
];

const PRIORITY_OPTIONS: { value: WishPriority; label: string }[] = [
  { value: "must", label: "必做" },
  { value: "want", label: "想要" },
  { value: "maybe", label: "也許" },
];

const FALLBACK_YEARS = (() => {
  const now = new Date().getUTCFullYear();
  return [now, now - 1, now - 2, now - 3, now - 4];
})();

export function WishArchiveFilter({
  value,
  onChange,
  availableYears,
  className,
}: WishArchiveFilterProps) {
  const years = availableYears.length > 0 ? availableYears : FALLBACK_YEARS;
  const toggleYear = (year: number) => {
    onChange({
      ...value,
      years: value.years.includes(year)
        ? value.years.filter((y) => y !== year)
        : [...value.years, year].sort((a, b) => b - a),
    });
  };
  const toggleCategory = (c: WishCategory) => {
    onChange({
      ...value,
      categories: value.categories.includes(c)
        ? value.categories.filter((x) => x !== c)
        : [...value.categories, c],
    });
  };
  const togglePriority = (p: WishPriority) => {
    onChange({
      ...value,
      priorities: value.priorities.includes(p)
        ? value.priorities.filter((x) => x !== p)
        : [...value.priorities, p],
    });
  };
  return (
    <aside
      data-component="WishArchiveFilter"
      data-testid="wish-archive-filter"
      className={cn(
        "bg-paper-elevated border border-hairline rounded-2xl p-s-4 flex flex-col gap-s-4",
        className,
      )}
    >
      <header>
        <h2 className="font-display text-body font-semibold text-ink-1">
          篩選
        </h2>
      </header>
      <FilterGroup label="完成年份">
        <div className="flex flex-wrap gap-1.5">
          {years.map((year) => {
            const selected = value.years.includes(year);
            return (
              <button
                key={year}
                type="button"
                onClick={() => toggleYear(year)}
                aria-pressed={selected}
                data-testid={`archive-filter-year-${year}`}
                className={cn(
                  "press min-h-11 px-s-3 rounded-full border text-meta",
                  selected
                    ? "border-ink-1 bg-ink-1 text-paper"
                    : "border-hairline text-ink-1",
                )}
              >
                {year}
              </button>
            );
          })}
        </div>
      </FilterGroup>
      <FilterGroup label="類別">
        <div className="flex flex-wrap gap-1.5">
          {CATEGORY_OPTIONS.map((opt) => {
            const selected = value.categories.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleCategory(opt.value)}
                aria-pressed={selected}
                data-testid={`archive-filter-category-${opt.value}`}
                className={cn(
                  "press min-h-11 px-s-3 rounded-full border text-meta",
                  selected
                    ? "border-ink-1 bg-ink-1 text-paper"
                    : "border-hairline text-ink-1",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </FilterGroup>
      <FilterGroup label="優先度">
        <div className="flex flex-wrap gap-1.5">
          {PRIORITY_OPTIONS.map((opt) => {
            const selected = value.priorities.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => togglePriority(opt.value)}
                aria-pressed={selected}
                data-testid={`archive-filter-priority-${opt.value}`}
                className={cn(
                  "press min-h-11 px-s-3 rounded-full border text-meta",
                  selected
                    ? "border-ink-1 bg-ink-1 text-paper"
                    : "border-hairline text-ink-1",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </FilterGroup>
      <FilterGroup label="完成日期範圍">
        <div className="flex items-center gap-s-2">
          <Calendar className="h-4 w-4 text-ink-3" aria-hidden />
          <input
            type="date"
            value={value.completedFrom}
            onChange={(e) => onChange({ ...value, completedFrom: e.target.value })}
            data-testid="archive-filter-completed-from"
            className="min-h-11 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
          />
          <span className="text-meta text-ink-3">至</span>
          <input
            type="date"
            value={value.completedTo}
            onChange={(e) => onChange({ ...value, completedTo: e.target.value })}
            data-testid="archive-filter-completed-to"
            className="min-h-11 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
          />
        </div>
      </FilterGroup>
      <button
        type="button"
        onClick={() =>
          onChange({
            years: [],
            categories: [],
            priorities: [],
            completedFrom: "",
            completedTo: "",
          })
        }
        data-testid="archive-filter-reset"
        className="press min-h-11 self-start px-s-4 rounded-full text-meta text-ink-2 hover:bg-paper-deep"
      >
        重置
      </button>
    </aside>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-s-2">
      <h3 className="text-meta font-semibold text-ink-2">{label}</h3>
      {children}
    </section>
  );
}
